import { DatePicker, Spinner, Stack, Text, PrimaryButton } from '@fluentui/react';
import { memo, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TIME_ENTRY_CORRECTIONS } from '../../constants/queryKeys';
import { ApiCalls } from '../../api/api';
import { CorrectionsGrid } from './components/Grid/CorrectionsGrid';
import { GridLayoutContextProvider } from './components/Grid/context/LayoutProvider';
import { CreateTimeEntryCorrectionModal } from './components/CreateTimeEntryCorrectionModal';

export const TimeEntryCorrections = () => {
    return <TimeEntryCorrectionsInner />;
};

export const TimeEntryCorrectionsInner = memo(function TimeEntryCorrectionsInner() {
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

    const { data, isLoading } = useQuery({
        queryKey: [TIME_ENTRY_CORRECTIONS, fromDate, toDate],
        queryFn: async () => {
            if (!fromDate || !toDate) {
                return null;
            }

            // Format dates as ISO strings for the API
            const fromDateISO = fromDate.getISOStringMidnight();
            const toDateISO = toDate.getISOStringMidnight();

            const response = await ApiCalls.timeEntryCorrectionsWithinDatesList({
                fromDate: fromDateISO,
                toDate: toDateISO,
            });
            return response.data;
        },
        enabled: !!(fromDate && toDate),
    });

    const renderGridContent = useMemo(() => {
        if (!fromDate || !toDate) {
            return (
                <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '200px' } }}>
                    <Text variant="large" styles={{ root: { color: '#666' } }}>
                        Please select both from and to dates to view corrections
                    </Text>
                </Stack>
            );
        }

        if (isLoading) {
            return <Spinner />;
        }

        if (data) {
            return <CorrectionsGrid data={data} />;
        }

        return (
            <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '200px' } }}>
                <Text variant="large" styles={{ root: { color: '#666' } }}>
                    No corrections found for the selected date range
                </Text>
            </Stack>
        );
    }, [fromDate, toDate, isLoading, data]);

    return (
        <Stack styles={{ root: { flexGrow: 1, height: '100%', padding: '30px 50px' } }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: { marginBottom: 20 } }}>
                <Stack>
                    <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
                        Time Entry Corrections (Preview)
                    </Text>
                    <span>Only time entries approved by the project manager are shown.</span>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="end">
                    <PrimaryButton text="Create new correction" onClick={() => setShowCreateModal(true)} iconProps={{ iconName: 'Add' }} />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <DatePicker
                        label="From Date"
                        value={fromDate}
                        onSelectDate={date => setFromDate(date)}
                        placeholder="Select start date"
                        ariaLabel="Select start date"
                        styles={{ root: { width: 150 } }}
                    />
                    <DatePicker
                        label="To Date"
                        value={toDate}
                        onSelectDate={date => setToDate(date)}
                        placeholder="Select end date"
                        ariaLabel="Select end date"
                        styles={{ root: { width: 150 } }}
                    />
                </Stack>
            </Stack>
            <GridLayoutContextProvider>{renderGridContent}</GridLayoutContextProvider>

            <CreateTimeEntryCorrectionModal isOpen={showCreateModal} onDismiss={() => setShowCreateModal(false)} />
        </Stack>
    );
});
